// The DRAFTING moment (prototype draftingBody): after "Draft the configuration", the assembly lines
// reveal one by one, then the console lands on the Skill step. Implemented as a simple staged reveal
// (state-driven, no bespoke keyframes — reduced-motion safe by construction). Presentation theatre
// ONLY: nothing here writes or invents configuration; the drafted values the following steps show
// come from the projection (G-182). Timers are cleaned up on unmount so a test never leaks a tick.

import { useEffect, useState } from 'react';
import { Icon } from '../../../../app/Icon.tsx';
import { Dot } from '../../../primitives.tsx';
import {
  DRAFT_REVEAL_HOLD_MS,
  DRAFT_REVEAL_ROWS,
  DRAFT_REVEAL_TICK_MS,
} from '../composer-model.ts';

export interface DraftingRevealProps {
  /** Called once every line has revealed and the short hold has elapsed. */
  readonly onDone: () => void;
}

/** The staged assembly reveal. */
export function DraftingReveal({ onDone }: DraftingRevealProps): React.JSX.Element {
  const [revealed, setRevealed] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setRevealed((count) => Math.min(count + 1, DRAFT_REVEAL_ROWS.length));
    }, DRAFT_REVEAL_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (revealed < DRAFT_REVEAL_ROWS.length) return;
    const hold = setTimeout(onDone, DRAFT_REVEAL_HOLD_MS);
    return () => clearTimeout(hold);
  }, [revealed, onDone]);

  return (
    <div className="py-6">
      <p className="ritona-numeric inline-flex items-center gap-2 text-sm text-forest">
        <Dot tone="go" />
        Ritona is assembling the configuration…
      </p>
      <ol aria-label="Drafting progress" className="mt-5 flex flex-col gap-3">
        {DRAFT_REVEAL_ROWS.slice(0, revealed).map((row) => (
          <li key={row} className="flex items-center gap-2.5 text-sm text-text-muted">
            <span className="text-forest">
              <Icon name="check" size={12} />
            </span>
            {row}
          </li>
        ))}
      </ol>
    </div>
  );
}
